import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DataTable } from './common/DataTable';
import { showDeleteConfirmation } from '../lib/sweetAlert';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';

interface Priest {
  _id: string;
  id?: string;
  name: string;
  email: string;
  phone?: string;
  temple?: {
    _id: string;
    name: string;
  };
  templeId?: string;
  isActive: boolean;
  role: string;
}

const PriestManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [priests, setPriests] = useState<Priest[]>([]);
  const [temples, setTemples] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedPriest, setSelectedPriest] = useState<Priest | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    templeId: '',
    password: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch temples first
      const templesRes = await adminApiClient.getTemples();
      const templesData = templesRes.success && templesRes.data?.temples ? templesRes.data.temples : [];

      if (templesRes.success && templesRes.data?.temples) {
        setTemples(templesRes.data.temples);
      }

      // Then fetch priests
      let priestsRes;
      if (user?.role === 'temple_admin' && user?.templeId) {
        priestsRes = await adminApiClient.getUsers({ role: 'priest' });
        if (priestsRes.success && priestsRes.data?.users) {
          priestsRes.data.users = priestsRes.data.users.filter((p: any) => p.templeId === user.templeId);
        }
      } else {
        priestsRes = await adminApiClient.getUsers({ role: 'priest' });
      }

      if (priestsRes.success && priestsRes.data?.users) {
        const priestsData = priestsRes.data.users;

        setPriests(priestsData.map((p: any) => {
          const templeId = p.templeId || p.temple?._id;
          const temple = templeId ? templesData.find((t: any) => t._id === templeId) : null;

          return {
            ...p,
            id: p._id,
            templeId: templeId,
            temple: temple ? { _id: temple._id, name: temple.name } : undefined
          };
        }));
      }

    } catch (err: any) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const loadingToast = toast.loading(selectedPriest ? 'Updating priest...' : 'Creating priest...');

    try {
      let response;
      if (selectedPriest) {
        response = await adminApiClient.updateUser(selectedPriest._id, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          templeId: formData.templeId
        });
      } else {
        response = await adminApiClient.createUser({
          ...formData,
          role: 'priest'
        });
      }

      if (response.success) {
        toast.success(selectedPriest ? 'Priest updated' : 'Priest created', { id: loadingToast });
        setShowForm(false);
        resetForm();
        fetchData();
      } else {
        toast.error(response.message || 'Operation failed', { id: loadingToast });
      }
    } catch (err: any) {
      toast.error(err.message || 'Error occurred', { id: loadingToast });
    }
  };

  const handleDelete = async (id: string) => {
    const isConfirmed = await showDeleteConfirmation('Remove Priest?', 'This account will be permanently deleted.');
    if (!isConfirmed) return;

    try {
      const response = await adminApiClient.deleteUser(id);
      if (response.success) {
        toast.success('Priest removed');
        fetchData();
      } else {
        toast.error('Failed to remove priest');
      }
    } catch (err) {
      toast.error('Error removing priest');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', templeId: '', password: '' });
    setSelectedPriest(null);
  };

  const handleEdit = (priest: Priest) => {
    setSelectedPriest(priest);
    setFormData({
      name: priest.name,
      email: priest.email,
      phone: priest.phone || '',
      templeId: priest.templeId || priest.temple?._id || '',
      password: ''
    });
    setShowForm(true);
  };

  const columns: ColumnDef<Priest>[] = [
    {
      accessorKey: 'name',
      header: 'Priest',
      cell: (info) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 font-bold">
            {info.getValue<string>().charAt(0)}
          </div>
          <div>
            <div className="font-medium text-gray-900">{info.getValue<string>()}</div>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'email',
      header: 'Contact',
      cell: (info) => (
        <div className="text-sm">
          <div className="flex items-center gap-1.5 text-gray-700">
            <Mail size={14} className="text-gray-400" /> {info.getValue<string>()}
          </div>
          {info.row.original.phone && (
            <div className="flex items-center gap-1.5 text-gray-500 mt-0.5">
              <Phone size={14} className="text-gray-400" /> {info.row.original.phone}
            </div>
          )}
        </div>
      )
    },
    {
      accessorKey: 'temple',
      header: 'Assigned Temple',
      cell: (info) => {
        const t = info.getValue() as { name: string } | undefined;
        return t ? (
          <span className="flex items-center gap-1.5 text-gray-700">
            <Building2 size={14} className="text-gray-400" /> {t.name}
          </span>
        ) : <span className="text-gray-400 italic">Unassigned</span>;
      }
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: (info) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${info.getValue() ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {info.getValue() ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex justify-end gap-2">
          <button onClick={() => handleEdit(info.row.original)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
            <Edit2 size={16} />
          </button>
          <button onClick={() => handleDelete(info.row.original._id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  const filteredPriests = priests.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Priest Management</h1>
          <p className="text-sm text-gray-500">Manage priests and temple assignments</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus size={18} /> Add Priest
        </button>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search priests..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
          />
        </div>
        <button onClick={fetchData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={filteredPriests}
          columns={columns}
          pagination={undefined} // Let DataTable handle state
          sorting={undefined}   // Let DataTable handle state
          enableClientSidePagination={true}
          loading={loading}
        />
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">{selectedPriest ? 'Edit Priest' : 'Add Priest'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full"><XCircle size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Temple</label>
                <select required value={formData.templeId} onChange={e => setFormData({ ...formData, templeId: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none">
                  <option value="">Select Temple</option>
                  {temples.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
              </div>
              {!selectedPriest && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input type="password" required value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none" placeholder="Min. 6 characters" minLength={6} />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t mt-6">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 text-white bg-orange-600 hover:bg-orange-700 rounded-lg">
                  {selectedPriest ? 'Update Priest' : 'create Priest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriestManagementPage;
